import { Component, createSignal, createEffect, onCleanup, For, Show } from 'solid-js';
import { selfServiceApi, LobbyTeam, SessionInfo } from '@/services/selfservice';
import LoadingSpinner from './LoadingSpinner';

interface GroupLobbyProps {
  sessionId: number;
  currentTeamId: number | null;
  isOrganizer: boolean;
  onGameStart: (session: SessionInfo) => void;
  onBack: () => void;
}

const GroupLobby: Component<GroupLobbyProps> = (props) => {
  const [teams, setTeams] = createSignal<LobbyTeam[]>([]);
  const [session, setSession] = createSignal<SessionInfo | null>(null);
  const [canStart, setCanStart] = createSignal(false);
  const [allTeamsReady, setAllTeamsReady] = createSignal(false);
  const [loading, setLoading] = createSignal(true);
  const [starting, setStarting] = createSignal(false);
  const [error, setError] = createSignal('');

  let pollInterval: number | undefined;

  const loadLobby = async () => {
    try {
      const response = await selfServiceApi.getLobby(props.sessionId);
      setTeams(response.teams);
      setSession(response.session);
      setCanStart(response.can_start);
      setAllTeamsReady(response.all_teams_ready);
      setError('');

      // If session became active, notify parent
      if (response.session.status === 'active') {
        props.onGameStart(response.session);
      }
    } catch (err: any) {
      const message = err.response?.data?.error || 'Błąd ładowania lobby';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Initial load and polling
  createEffect(() => {
    loadLobby();

    // Poll every 3 seconds for updates
    pollInterval = window.setInterval(() => {
      if (!starting()) {
        loadLobby();
      }
    }, 3000);
  });

  onCleanup(() => {
    if (pollInterval) {
      clearInterval(pollInterval);
    }
  });

  const handleStartGame = async () => {
    if (!canStart() || starting()) return;

    setStarting(true);
    setError('');

    try {
      const response = await selfServiceApi.startGame(props.sessionId);
      props.onGameStart(response.session);
    } catch (err: any) {
      const message = err.response?.data?.error || 'Błąd uruchamiania gry';
      setError(message);
      setStarting(false);
    }
  };

  const getTeamStatusIcon = (team: LobbyTeam) => {
    if (!team.is_registered) return '⏳';
    if (team.is_organizer) return '👑';
    return '✅';
  };

  const getTeamStatusClass = (team: LobbyTeam) => {
    if (!team.is_registered) {
      return 'bg-gray-500/20 border-gray-500/30';
    }
    if (team.is_organizer) {
      return 'bg-yellow-500/20 border-yellow-500/50';
    }
    return 'bg-green-500/20 border-green-500/50';
  };

  const registeredCount = () => teams().filter(t => t.is_registered).length;
  const totalCount = () => teams().length;

  return (
    <div class="space-y-6">
      {/* Header */}
      <div class="text-center">
        <div class="text-6xl mb-4">👥</div>
        <h2 class="text-xl font-semibold text-white mb-2">
          Poczekalnia grupy
        </h2>
        <p class="text-gray-300 text-sm">
          Czekamy na pozostałych graczy...
        </p>
      </div>

      {/* Progress indicator */}
      <div class="bg-white/5 rounded-xl p-4">
        <div class="flex justify-between items-center mb-3">
          <span class="text-gray-300 text-sm">Zalogowanych zespołów</span>
          <span class="text-white font-bold text-lg">
            {registeredCount()}/{totalCount()}
          </span>
        </div>
        <div class="w-full bg-white/10 rounded-full h-3 overflow-hidden">
          <div
            class="h-full bg-gradient-to-r from-green-500 to-emerald-400 transition-all duration-500"
            style={{ width: `${totalCount() > 0 ? (registeredCount() / totalCount()) * 100 : 0}%` }}
          />
        </div>
      </div>

      {/* Loading */}
      <Show when={loading()}>
        <div class="flex flex-col items-center justify-center py-8">
          <LoadingSpinner size="lg" />
          <p class="text-gray-400 mt-4">Ładowanie...</p>
        </div>
      </Show>

      {/* Teams list */}
      <Show when={!loading()}>
        <div class="space-y-3">
          <h3 class="text-sm font-medium text-gray-400 uppercase tracking-wider">
            Zespoły ({registeredCount()}/{totalCount()})
          </h3>

          <div class="space-y-2">
            <For each={teams()}>
              {(team) => (
                <div
                  class={`flex items-center justify-between p-4 rounded-xl border transition-all ${getTeamStatusClass(team)}`}
                >
                  <div class="flex items-center gap-3">
                    <span class="text-2xl">{getTeamStatusIcon(team)}</span>
                    <div>
                      <div class="flex items-center gap-2">
                        <span class="text-white font-medium">
                          {team.is_registered ? team.team_name : `Zespół #${team.team_number}`}
                        </span>
                        <Show when={team.is_organizer}>
                          <span class="text-xs bg-yellow-500/30 text-yellow-200 px-2 py-0.5 rounded-full">
                            Organizator
                          </span>
                        </Show>
                      </div>
                      <span class="text-sm text-gray-400">
                        {team.is_registered ? 'Gotowy do gry' : 'Oczekuje na rejestrację'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </For>
          </div>
        </div>
      </Show>

      {/* Error */}
      <Show when={error()}>
        <div class="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-200 text-sm text-center">
          {error()}
        </div>
      </Show>

      {/* Waiting message for non-organizers */}
      <Show when={!props.isOrganizer && !allTeamsReady()}>
        <div class="bg-blue-500/20 border border-blue-500/50 rounded-lg p-4 text-center">
          <p class="text-blue-200 text-sm">
            <span class="text-xl mr-2">⏳</span>
            Czekaj na pozostałych graczy i start organizatora
          </p>
        </div>
      </Show>

      {/* Waiting for organizer - all teams ready */}
      <Show when={!props.isOrganizer && allTeamsReady()}>
        <div class="bg-green-500/20 border border-green-500/50 rounded-lg p-4 text-center">
          <p class="text-green-200 text-sm">
            <span class="text-xl mr-2">✅</span>
            Wszyscy są gotowi! Czekaj na start organizatora...
          </p>
        </div>
      </Show>

      {/* Organizer controls */}
      <Show when={props.isOrganizer}>
        <div class="space-y-4 pt-4 border-t border-white/10">
          <Show when={!allTeamsReady()}>
            <div class="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-4 text-center">
              <p class="text-yellow-200 text-sm">
                <span class="text-xl mr-2">👑</span>
                Jesteś organizatorem. Poczekaj aż wszyscy dołączą.
              </p>
            </div>
          </Show>

          <Show when={allTeamsReady()}>
            <div class="bg-green-500/20 border border-green-500/50 rounded-lg p-4 text-center mb-4">
              <p class="text-green-200 text-sm">
                <span class="text-xl mr-2">🎉</span>
                Wszyscy gotowi! Możesz rozpocząć grę.
              </p>
            </div>
          </Show>

          <button
            onClick={handleStartGame}
            disabled={!canStart() || starting()}
            class={`w-full py-5 px-6 text-xl font-bold rounded-xl transition-all transform
              ${canStart() && !starting()
                ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 hover:scale-[1.02] shadow-lg shadow-green-500/30'
                : 'bg-gray-600 text-gray-400 cursor-not-allowed'
              }
            `}
          >
            <Show when={starting()} fallback={
              <>
                <span class="mr-2">🚀</span>
                {canStart() ? 'START GRY' : 'Czekaj na wszystkich'}
              </>
            }>
              <span class="flex items-center justify-center gap-3">
                <LoadingSpinner size="sm" />
                Uruchamiam grę...
              </span>
            </Show>
          </button>
        </div>
      </Show>

      {/* Back button */}
      <button
        onClick={props.onBack}
        class="w-full py-3 px-4 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-all text-sm"
      >
        ← Wróć
      </button>

      {/* Auto-refresh indicator */}
      <div class="text-center text-gray-500 text-xs">
        <span class="inline-flex items-center gap-1">
          <span class="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
          Lista odświeża się automatycznie
        </span>
      </div>
    </div>
  );
};

export default GroupLobby;
